import { DateTime } from "@bps/date-time";
import { AxiosInstance } from "@bps/http-client";
import { SiteSyncStateDto } from "@libs/api/gateways/sia/models/SiteSyncState";
import { EntitySyncStateDto } from "@libs/api/gateways/tg/model/EntitySyncState";
import {
  PremSettings,
  PremSettingsApiVersion,
  PremSettingsDto,
  PremSettingsUpdate
} from "@libs/api/gateways/tg/tg-ops-gateway.dtos";
import { ITgOpsGateway } from "@libs/api/gateways/tg/tg-ops-gateway.interface";

export class TgOpsGateway implements ITgOpsGateway {
  constructor(private api: AxiosInstance) {}

  async getReplicationSyncState(siteId: string): Promise<SiteSyncStateDto> {
    const config = {
      headers: {
        "X-Tenant": siteId
      }
    };

    const { data } = await this.api.get<SiteSyncStateDto>(
      "/siteState/sync",
      config
    );
    return data;
  }

  async recalculateAvailability(siteId: string) {
    const config = {
      headers: {
        "X-Tenant": siteId
      }
    };
    await this.api.post("/availability/recalculate", null, config);
    return "Ok";
  }

  async snapshotTables(siteId: string): Promise<string> {
    const config = {
      headers: {
        "X-Tenant": siteId
      }
    };

    const { data } = await this.api.post(
      `/siteState/snapshot/${siteId}`,
      null,
      config
    );
    return data;
  }

  async getEntitySyncState(siteId: string): Promise<EntitySyncStateDto> {
    const config = {
      headers: {
        "X-Tenant": siteId
      }
    };

    const { data } = await this.api.get<EntitySyncStateDto>(
      "/entitySyncState",
      config
    );
    return data;
  }

  async getPremSettings(
    siteId: string,
    apiVersion: PremSettingsApiVersion
  ): Promise<PremSettings[]> {
    const config = {
      headers: {
        "X-Tenant": siteId
      },
      params: {
        tenantId: siteId
      }
    };

    const endpoint = apiVersion === "v2" ? "/premSettings/v2" : "/premSettings";

    const { data } = await this.api.get<PremSettingsDto[]>(endpoint, config);
    return data.map(({ ...dto }) => {
      const { created, updated, ...rest } = dto;
      return {
        created: DateTime.fromISO(created),
        updated: DateTime.fromISO(updated),
        ...rest
      };
    });
  }

  async updatePremSettings(
    siteId: string,
    updates: PremSettingsUpdate[]
  ): Promise<PremSettings[]> {
    const config = {
      headers: {
        "X-Tenant": siteId
      },
      params: {
        tenantId: siteId
      }
    };

    const { data } = await this.api.put<PremSettingsDto[]>(
      "/premSettings",
      updates,
      config
    );

    return data.map(({ ...dto }) => {
      const { created, updated, ...rest } = dto;
      return {
        created: DateTime.fromISO(created),
        updated: DateTime.fromISO(updated),
        ...rest
      };
    });
  }
}
